import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import expendituresService from '../services/expendituresService';
import propertiesService from '../services/propertiesService';
import styles from './PropertyExpenditures.module.css';
import Loader from '../components/Loader';
import Breadcrumbs from '../components/Breadcrumbs';

const PropertyExpenditures = () => {
  const params = useParams();
  const propertyId = params.propertyId || params.id; // Handle both :propertyId and :id routes
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expenditures, setExpenditures] = useState([]);
  const [property, setProperty] = useState(null);
  const [selectedExpenditures, setSelectedExpenditures] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  // Filter state (matching Laravel structure)
  const [filters, setFilters] = useState({
    category_id: '',
    type_id: '',
    bank_account_id: '',
    creditor: '',
    date_from: '',
    date_to: ''
  });

  // Applied filters state - only updated when search is clicked
  const [appliedFilters, setAppliedFilters] = useState({
    category_id: '',
    type_id: '',
    bank_account_id: '',
    creditor: '',
    date_from: '',
    date_to: ''
  });

  // State for dropdowns
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // State for DataTables controls
  const [tableControls, setTableControls] = useState({
    entriesPerPage: '30',
    search: ''
  });

  // Applied search state - updated on change
  const [appliedTableSearch, setAppliedTableSearch] = useState('');

  // State for column visibility (matching Laravel structure)
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    category: true,
    type: true,
    status: true,
    bankAccount: true,
    amount: true,
    date: true,
    creditor: true,
    invoice: true,
    order: true,
    actions: true
  });

  // Refs for date pickers
  const dateFromInputRef = useRef(null);
  const dateToInputRef = useRef(null);

  useEffect(() => {
    if (propertyId) {
      loadDropdownData();
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, pagination.current_page, appliedFilters, appliedTableSearch, tableControls.entriesPerPage]);

  // Load dropdown data (categories, types, bank accounts)
  const loadDropdownData = async () => {
    try {
      const [categoriesResponse, typesResponse, bankAccountsResponse] = await Promise.all([
        expendituresService.getCategories(),
        expendituresService.getTypes(),
        expendituresService.getBankAccounts()
      ]);

      if (categoriesResponse && categoriesResponse.success) {
        setCategories(categoriesResponse.data || []);
      }
      if (typesResponse && typesResponse.success) {
        setTypes(typesResponse.data || []);
      }
      if (bankAccountsResponse && bankAccountsResponse.success) {
        setBankAccounts(bankAccountsResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      // Set empty arrays on error
      setCategories([]);
      setTypes([]);
      setBankAccounts([]);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load property data
      try {
        const propertyResponse = await propertiesService.getProperty(propertyId);
        if (propertyResponse && propertyResponse.success && propertyResponse.data) {
          setProperty(propertyResponse.data.property || propertyResponse.data);
        }
      } catch (propertyError) {
        console.error('Error loading property data:', propertyError);
        // Continue without property data
      }

      // Load expenditures data with applied filters and search
      // Filter out empty values and "All" options before sending to backend
      const cleanFilters = Object.entries(appliedFilters).reduce((acc, [key, value]) => {
        // Skip empty strings, null, undefined, and "All" values
        if (value && value !== '' && value !== 'All' && value !== 'all') {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      // Add search if it exists
      if (appliedTableSearch && appliedTableSearch.trim() !== '') {
        cleanFilters.search = appliedTableSearch.trim();
      }
      
      console.log('Loading expenditures for property:', propertyId, 'with clean filters:', cleanFilters);
      const response = await expendituresService.getPropertyExpenditureItems(propertyId, {
        page: pagination.current_page,
        per_page: parseInt(tableControls.entriesPerPage, 10) || pagination.per_page,
        ...cleanFilters
      });
      console.log('Expenditures API response:', response);

      if (response && response.success) {
        // Handle DataTables response format
        if (response.data && Array.isArray(response.data)) {
          setExpenditures(response.data);
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          setExpenditures(response.data.data);
        } else {
          setExpenditures([]);
        }
        
        // Handle pagination from DataTables format
        if (response.recordsTotal !== undefined) {
          const perPage = parseInt(tableControls.entriesPerPage, 10) || pagination.per_page;
          setPagination(prev => ({
            ...prev,
            // Only recalculate current_page if backend provides 'start' value, otherwise preserve state
            current_page: response.start !== undefined ? Math.floor(response.start / perPage) + 1 : prev.current_page,
            last_page: Math.ceil((response.recordsFiltered || response.recordsTotal || 0) / perPage),
            total: response.recordsTotal || response.recordsFiltered || 0
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            current_page: response.current_page || 1,
            last_page: response.last_page || 1,
            total: response.total || 0
          }));
        }
      } else {
        // No data available
        setExpenditures([]);
        setPagination(prev => ({
          ...prev,
          current_page: 1,
          last_page: 1,
          total: 0
        }));
      }
    } catch (error) {
      console.error('Error loading property expenditures:', error);
      setExpenditures([]);
      setPagination(prev => ({
        ...prev,
        current_page: 1,
        last_page: 1,
        total: 0
      }));
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Apply filters when Search button is clicked
    console.log('Applying filters:', filters);
    setAppliedFilters(filters);
    setPagination(prev => ({
      ...prev,
      current_page: 1
    }));
  };

  // Date picker handlers
  const handleCalendarIconClick = (field, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const ref = field === 'date_from' ? dateFromInputRef : dateToInputRef;
    if (ref.current) {
      // Use setTimeout to ensure it's a direct user gesture
      setTimeout(() => {
        try {
          if (ref.current.showPicker) {
            ref.current.showPicker();
          } else {
            ref.current.click();
          }
        } catch (error) {
          // Fallback: just click the input
          ref.current.click();
        }
      }, 0);
    }
  };

  // Handle text input click - open date picker
  const handleDateInputClick = (field, e) => {
    e.preventDefault();
    const ref = field === 'date_from' ? dateFromInputRef : dateToInputRef;
    if (ref.current) {
      // Directly trigger click on the date input
      ref.current.click();
    }
  };

  // Handle date input change (from native date picker)
  const handleDateChange = (field, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      // Convert from yyyy-mm-dd to mm/dd/yyyy (Laravel format)
      const [year, month, day] = dateValue.split('-');
      const formattedDate = `${month}/${day}/${year}`;
      handleFilterChange(field, formattedDate);
    } else {
      handleFilterChange(field, '');
    }
  };

  // Convert date format for date input (mm/dd/yyyy to yyyy-mm-dd)
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // Handle mm/dd/yyyy format
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    // Handle yyyy-mm-dd format (already correct)
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    return '';
  };

  // Handle table control changes
  const handleTableControlChange = (field, value) => {
    setTableControls(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'entriesPerPage') {
      setPagination(prev => ({
        ...prev,
        current_page: 1
      }));
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setTableControls(prev => ({
      ...prev,
      search: e.target.value
    }));
  };

  // Apply search when user stops typing (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedTableSearch(tableControls.search);
      setPagination(prev => ({
        ...prev,
        current_page: 1
      }));
    }, 300);

    return () => clearTimeout(timer);
  }, [tableControls.search]);

  // Handle export CSV (matching Laravel column structure)
  const handleExportCSV = async () => {
    try {
      const csvData = expenditures.map(exp => ({
        Category: exp.categoryName || exp.category?.name || exp.type?.category?.name || '-',
        Type: exp.typeName || exp.type?.name || '-',
        Status: getStatusText(exp.status, exp.due_date),
        'GG Bank Account': exp.bankAccountName || exp.bank_account?.name || exp.bankAccount?.name || '-',
        Amount: parseFloat(exp.amount || 0).toFixed(2), // Remove £ symbol for CSV compatibility
        Date: formatDate(exp.date || exp.due_date),
        Creditor: exp.creditor || '-',
        Invoice: exp.invoice_number || '-',
        Order: exp.purchase_order_number || '-'
      }));

      const headers = Object.keys(csvData[0] || {});
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes
          const escapedValue = String(value).replace(/"/g, '""');
          return `"${escapedValue}"`;
        }).join(','))
      ].join('\n');

      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `property-expenditures-${propertyId}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Handle export PDF
  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      
      // Create PDF document in landscape orientation
      let doc;
      try {
        // Try newer API first
        doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });
      } catch (e) {
        console.log("New API failed, trying legacy API:", e);
        // Fallback to legacy API
        doc = new jsPDF("landscape", "mm", "a4");
      }
      
      // Get landscape page dimensions (A4 landscape: 297mm x 210mm)
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      doc.setFontSize(16);
      doc.text(`Property Expenditures - ${property?.name || `Property ${propertyId}`}`, 14, 15);
      
      doc.setFontSize(10);
      let y = 25;
      const margin = 14;
      const rowHeight = 7;
      
      // Table headers (matching Laravel structure) - adjusted column widths for landscape
      doc.setFont(undefined, 'bold');
      const headers = ['Category', 'Type', 'Status', 'GG Bank Account', 'Amount', 'Date', 'Creditor', 'Invoice', 'Order'];
      // Increased column widths for landscape orientation (more horizontal space available)
      const colWidths = [30, 30, 25, 40, 25, 30, 40, 25, 25];
      let x = margin;
      
      headers.forEach((header, i) => {
        doc.text(header, x, y);
        x += colWidths[i];
      });
      
      y += rowHeight;
      doc.setFont(undefined, 'normal');
      
      // Table rows
      expenditures.forEach(exp => {
        if (y > pageHeight - 20) {
          doc.addPage();
          y = 15;
        }
        
        x = margin;
        const rowData = [
          exp.categoryName || exp.category?.name || exp.type?.category?.name || '-',
          exp.typeName || exp.type?.name || '-',
          getStatusText(exp.status, exp.due_date),
          exp.bankAccountName || exp.bank_account?.name || exp.bankAccount?.name || '-',
          `£${parseFloat(exp.amount || 0).toFixed(2)}`,
          formatDate(exp.date || exp.due_date),
          exp.creditor || '-',
          exp.invoice_number || '-',
          exp.purchase_order_number || '-'
        ];
        
        rowData.forEach((data, i) => {
          // Truncate text to fit column width (landscape allows more characters per column)
          const maxChars = Math.floor(colWidths[i] / 2.5); // More characters in landscape
          doc.text(String(data).substring(0, maxChars), x, y);
          x += colWidths[i];
        });
        
        y += rowHeight;
      });
      
      doc.save(`property-expenditures-${propertyId}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Handle print table
  const handlePrintTable = () => {
    const printWindow = window.open('', '_blank');
    const printHTML = `
      <html>
        <head>
          <title>Property Expenditures - ${property?.name || `Property ${propertyId}`}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            h1 { margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <h1>Property Expenditures - ${property?.name || `Property ${propertyId}`}</h1>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Type</th>
                <th>Status</th>
                <th>GG Bank Account</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Creditor</th>
                <th>Invoice</th>
                <th>Order</th>
              </tr>
            </thead>
            <tbody>
              ${expenditures.map(exp => `
                <tr>
                  <td>${exp.categoryName || exp.category?.name || exp.type?.category?.name || '-'}</td>
                  <td>${exp.typeName || exp.type?.name || '-'}</td>
                  <td>${getStatusText(exp.status, exp.due_date)}</td>
                  <td>${exp.bankAccountName || exp.bank_account?.name || exp.bankAccount?.name || '-'}</td>
                  <td>£${parseFloat(exp.amount || 0).toFixed(2)}</td>
                  <td>${formatDate(exp.date || exp.due_date)}</td>
                  <td>${exp.creditor || '-'}</td>
                  <td>${exp.invoice_number || '-'}</td>
                  <td>${exp.purchase_order_number || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.print();
  };

  // Handle column visibility toggle
  const toggleColumnVisibility = (column) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      current_page: page
    }));
  };

  const handleEdit = (expenditure) => {
    navigate(`/properties/${propertyId}/expenditures/items/${expenditure.id}/edit`);
  };

  const handleDelete = async (expenditure) => {
    if (window.confirm('Are you sure you want to delete this expenditure?')) {
      try {
        const response = await expendituresService.deleteExpenditure(propertyId, expenditure.id);
        if (response && response.success) {
          toast.success('Expenditure deleted successfully');
          loadData();
        } else {
          toast.error(response?.message || 'Failed to delete expenditure');
        }
      } catch (error) {
        console.error('Error deleting expenditure:', error);
        toast.error('Failed to delete expenditure');
      }
    }
  };

  const handleSelectExpenditure = (expenditureId) => {
    setSelectedExpenditures(prev => {
      if (prev.includes(expenditureId)) {
        return prev.filter(id => id !== expenditureId);
      } else {
        return [...prev, expenditureId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedExpenditures(expenditures.map(exp => exp.id));
    } else {
      setSelectedExpenditures([]);
    }
  };

  const handleToggleVoid = async () => {
    if (selectedExpenditures.length === 0) {
      toast.error('Please select at least one expenditure to void/unvoid');
      return;
    }

    try {
      const promises = selectedExpenditures.map(expenditureId =>
        expendituresService.toggleVoid(expenditureId)
      );

      await Promise.all(promises);

      toast.success('Expenditure void status updated successfully');
      setSelectedExpenditures([]);
      loadData();
    } catch (error) {
      console.error('Error toggling void status:', error);
      toast.error('Failed to update void status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Backend returns dates in DD/MM/YYYY format (en-GB locale)
    // Check if it's already in DD/MM/YYYY format
    if (typeof dateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString; // Already formatted, return as is
    }
    
    // Try to parse as ISO date (YYYY-MM-DD) or other formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '-'; // Invalid date
    }
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Map numeric status to text (Laravel: config('expenditures.status_reverse'))
  // status 0 = pending/not paid, status 1 = paid
  // Overdue = status 0 AND due_date < today
  const getStatusText = (status, dueDate) => {
    if (status === 1) {
      return 'paid';
    }
    
    // Check if overdue: status 0 (not paid) AND due_date exists AND due_date < today
    if (status === 0 && dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Parse due_date (could be DD/MM/YYYY or Date object)
      let dueDateObj;
      if (typeof dueDate === 'string') {
        // Try parsing DD/MM/YYYY format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dueDate)) {
          const [day, month, year] = dueDate.split('/');
          dueDateObj = new Date(year, month - 1, day);
        } else {
          dueDateObj = new Date(dueDate);
        }
      } else {
        dueDateObj = new Date(dueDate);
      }
      
      dueDateObj.setHours(0, 0, 0, 0);
      
      if (!isNaN(dueDateObj.getTime()) && dueDateObj < today) {
        return 'overdue';
      }
    }
    
    return 'pending';
  };

  const getStatusBadge = (expenditure) => {
    // Calculate status: check if overdue based on status and due_date
    const statusValue = getStatusText(expenditure.status, expenditure.due_date);
    
    const statusClasses = {
      paid: styles.badgeSuccess,
      pending: styles.badgeWarning,
      overdue: styles.badgeDanger
    };
    
    return (
      <span className={`${styles.badge} ${statusClasses[statusValue] || styles.badgeSecondary}`}>
        {statusValue.toUpperCase()}
      </span>
    );
  };

  if (initialLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs - Laravel equivalent: Breadcrumbs::render('property.expenditures', $property, $currentUser) */}
      <Breadcrumbs
        items={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Properties', href: '/properties' },
          { label: property?.name || `Property ${propertyId}`, href: `/properties/${propertyId}` },
          { label: 'Property Expenditures', active: true }
        ]}
      />

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>Property Expenditures</h1>
          <p className={styles.subtitle}>
            {property?.name || `Property ${propertyId}`}
          </p>
        </div>
        <div className={styles.actions}>
          <Link 
            to="/expenditures/create" 
            className={`${styles.btn} ${styles.btnPrimary}`}
          >
            Add New Expenditure
          </Link>
        </div>
      </div>

      {/* Void Button Section (matching Laravel) */}
      {selectedExpenditures.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <button
            type="button"
            className="btn btn-warning"
            onClick={handleToggleVoid}
            style={{
              backgroundColor: '#f0ad4e',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Void/Unvoid Selected ({selectedExpenditures.length})
          </button>
        </div>
      )}

      {/* Filters - Matching Laravel structure */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.filterForm}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Category</label>
              <select
                className={styles.filterSelect}
                value={filters.category_id}
                onChange={(e) => handleFilterChange('category_id', e.target.value)}
              >
                <option value="">All</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Type</label>
              <select
                className={styles.filterSelect}
                value={filters.type_id}
                onChange={(e) => handleFilterChange('type_id', e.target.value)}
              >
                <option value="">All</option>
                {types.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Bank Account</label>
              <select
                className={styles.filterSelect}
                value={filters.bank_account_id}
                onChange={(e) => handleFilterChange('bank_account_id', e.target.value)}
              >
                <option value="">All</option>
                {bankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Creditor</label>
              <input
                type="text"
                className={styles.filterInput}
                value={filters.creditor}
                onChange={(e) => handleFilterChange('creditor', e.target.value)}
                placeholder=""
              />
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Date from</label>
              <div style={{ position: 'relative', width: '100%', display: 'inline-block' }}>
                {/* Text input for display - behind date input */}
                <input
                  type="text"
                  className={styles.filterInput}
                  value={filters.date_from}
                  placeholder="mm/dd/yyyy"
                  style={{ paddingRight: '35px', cursor: 'pointer', pointerEvents: 'none' }}
                  readOnly
                  tabIndex={-1}
                />
                {/* Date input overlay that covers entire input area - captures all clicks */}
                <input
                  ref={dateFromInputRef}
                  type="date"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 10,
                    fontSize: '16px', // Prevents zoom on iOS
                    padding: '8px 35px 8px 12px', // Match text input padding
                    margin: 0,
                    border: 'none',
                    background: 'transparent',
                    boxSizing: 'border-box'
                  }}
                  value={convertToDateInputFormat(filters.date_from)}
                  onChange={(e) => handleDateChange('date_from', e)}
                />
                <span 
                  className="glyphicon glyphicon-calendar" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (dateFromInputRef.current) {
                      dateFromInputRef.current.showPicker?.() || dateFromInputRef.current.click();
                    }
                  }}
                  style={{ 
                    position: 'absolute', 
                    right: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: '#999',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    zIndex: 11
                  }} 
                />
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Date to</label>
              <div style={{ position: 'relative', width: '100%', display: 'inline-block' }}>
                {/* Text input for display - behind date input */}
                <input
                  type="text"
                  className={styles.filterInput}
                  value={filters.date_to}
                  placeholder="mm/dd/yyyy"
                  style={{ paddingRight: '35px', cursor: 'pointer', pointerEvents: 'none' }}
                  readOnly
                  tabIndex={-1}
                />
                {/* Date input overlay that covers entire input area - captures all clicks */}
                <input
                  ref={dateToInputRef}
                  type="date"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 10,
                    fontSize: '16px', // Prevents zoom on iOS
                    padding: '8px 35px 8px 12px', // Match text input padding
                    margin: 0,
                    border: 'none',
                    background: 'transparent',
                    boxSizing: 'border-box'
                  }}
                  value={convertToDateInputFormat(filters.date_to)}
                  onChange={(e) => handleDateChange('date_to', e)}
                />
                <span 
                  className="glyphicon glyphicon-calendar" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (dateToInputRef.current) {
                      dateToInputRef.current.showPicker?.() || dateToInputRef.current.click();
                    }
                  }}
                  style={{ 
                    position: 'absolute', 
                    right: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: '#999',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    zIndex: 11
                  }} 
                />
              </div>
            </div>
          </div>

          <div className={styles.searchButtonContainer}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`}>
              Search
            </button>
          </div>
        </form>
      </div>

      {/* DataTables Controls */}
      <div className={styles.dataTableControls}>
        <div className={styles.entriesControl}>
          <label>
            Show{' '}
            <select
              value={tableControls.entriesPerPage}
              onChange={(e) => handleTableControlChange('entriesPerPage', e.target.value)}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="75">75</option>
              <option value="100">100</option>
            </select>{' '}
            entries
          </label>
        </div>

        <div className={styles.searchControl}>
          <label>
            Search:{' '}
            <input
              type="text"
              value={tableControls.search}
              onChange={handleSearchInputChange}
              placeholder="Search expenditures..."
              autoComplete="off"
            />
          </label>
        </div>

        <div className={styles.actionButtons}>
          <button 
            className={styles.exportBtn}
            onClick={handleExportCSV}
            title="Export as CSV"
          >
            Export as CSV
          </button>
          <button 
            className={styles.exportBtn}
            onClick={handleExportPDF}
            title="Export as PDF"
          >
            Export as PDF
          </button>
          <button 
            className={styles.exportBtn}
            onClick={handlePrintTable}
            title="Print table"
          >
            Print table
          </button>
          <div className={styles.visibilityBtnContainer}>
            <button 
              className={styles.visibilityBtn}
              onClick={() => setShowColumnVisibility(!showColumnVisibility)}
            >
              Column visibility
            </button>
            {showColumnVisibility && (
              <div className={styles.columnVisibilityDropdown}>
                <h4>Toggle Columns:</h4>
                {Object.entries(columnVisibility).map(([column, visible]) => {
                  // Map column keys to display names matching Laravel
                  const columnNames = {
                    category: 'Category',
                    type: 'Type',
                    status: 'Status',
                    bankAccount: 'GG Bank Account',
                    amount: 'Amount',
                    date: 'Date',
                    creditor: 'Creditor',
                    invoice: 'Invoice',
                    order: 'Order',
                    actions: 'Actions'
                  };
                  return (
                    <label key={column} className={styles.columnToggle}>
                      <input
                        type="checkbox"
                        checked={visible}
                        onChange={() => toggleColumnVisibility(column)}
                      />
                      <span>{columnNames[column] || column.charAt(0).toUpperCase() + column.slice(1)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expenditures Table */}
      <div className={styles.tableContainer} style={{ position: 'relative' }}>
        {loading && expenditures.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div style={{ fontSize: '16px', color: '#333' }}>Loading...</div>
          </div>
        )}
        <table className={styles.table} style={{ opacity: loading && expenditures.length > 0 ? 0.5 : 1 }}>
          <thead>
            <tr>
              {columnVisibility.category && <th>Category</th>}
              {columnVisibility.type && <th>Type</th>}
              {columnVisibility.status && <th>Status</th>}
              {columnVisibility.bankAccount && <th>GG Bank Account</th>}
              {columnVisibility.amount && <th>Amount</th>}
              {columnVisibility.date && <th>Date</th>}
              {columnVisibility.creditor && <th>Creditor</th>}
              {columnVisibility.invoice && <th>Invoice</th>}
              {columnVisibility.order && <th>Order</th>}
              {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {expenditures.length === 0 ? (
              <tr>
                <td colSpan={Object.values(columnVisibility).filter(v => v).length} className={styles.noData}>
                  No expenditures found
                </td>
              </tr>
            ) : (
              expenditures
                .filter(exp => {
                  if (!appliedTableSearch) return true;
                  const search = appliedTableSearch.toLowerCase();
                  return (
                    (exp.categoryName || exp.category?.name || exp.type?.category?.name || '').toLowerCase().includes(search) ||
                    (exp.typeName || exp.type?.name || '').toLowerCase().includes(search) ||
                    (exp.creditor || '').toLowerCase().includes(search) ||
                    (exp.invoice_number || '').toLowerCase().includes(search) ||
                    (exp.purchase_order_number || '').toLowerCase().includes(search) ||
                    formatCurrency(exp.amount).toLowerCase().includes(search) ||
                    (exp.bankAccountName || exp.bank_account?.name || exp.bankAccount?.name || '').toLowerCase().includes(search)
                  );
                })
                .map((expenditure) => (
                  <tr key={expenditure.id} style={{ backgroundColor: expenditure.is_void || expenditure.void ? '#ffffcc' : 'transparent' }}>
                    {columnVisibility.category && (
                      <td>{expenditure.categoryName || expenditure.category?.name || expenditure.type?.category?.name || '-'}</td>
                    )}
                    {columnVisibility.type && <td>{expenditure.typeName || expenditure.type?.name || '-'}</td>}
                    {columnVisibility.status && <td>{getStatusBadge(expenditure)}</td>}
                    {columnVisibility.bankAccount && (
                      <td>{expenditure.bankAccountName || expenditure.bank_account?.name || expenditure.bankAccount?.name || '-'}</td>
                    )}
                    {columnVisibility.amount && <td>{formatCurrency(expenditure.amount)}</td>}
                    {columnVisibility.date && <td>{formatDate(expenditure.date || expenditure.due_date)}</td>}
                    {columnVisibility.creditor && <td>{expenditure.creditor || '-'}</td>}
                    {columnVisibility.invoice && <td>{expenditure.invoice_number || '-'}</td>}
                    {columnVisibility.order && <td>{expenditure.purchase_order_number || '-'}</td>}
                    {columnVisibility.actions && (
                      <td className={styles.actions}>
                        <button
                          onClick={() => handleEdit(expenditure)}
                          className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(expenditure)}
                          className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={styles.pagination}>
        <div className={styles.pageInfo}>
          Showing {((pagination.current_page - 1) * parseInt(tableControls.entriesPerPage, 10)) + 1} to{' '}
          {Math.min(pagination.current_page * parseInt(tableControls.entriesPerPage, 10), pagination.total)} of{' '}
          {pagination.total} entries
        </div>
        
        {pagination.last_page > 1 && (
          <div className={styles.paginationControls}>
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className={`${styles.btn} ${styles.btnSecondary}`}
            >
              Previous
            </button>
            
            <span className={styles.pageNumbers}>
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                .filter(page => {
                  const current = pagination.current_page;
                  return page === 1 || page === pagination.last_page || (page >= current - 2 && page <= current + 2);
                })
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  const showEllipsis = prevPage && page - prevPage > 1;
                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && <span>...</span>}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`${styles.pageBtn} ${pagination.current_page === page ? styles.activePage : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
            </span>
            
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className={`${styles.btn} ${styles.btnSecondary}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyExpenditures; 